import {
  Timeline,
  TimelineStep,
  type TimelineStepState,
  type TimelineStepTone,
} from '@/components/ui/timeline'
import { formatDate } from '@/lib/formatters'
import { type PolicyChangeRequest, PolicyChangeStatus } from '@/models/policyChange'
import { WITHDRAWN_LABEL } from './policyChangeCopy'

/** How a request closed with nothing changed marks the step it stopped at. */
const STOPPED: Partial<Record<PolicyChangeStatus, { title: string; tone: TimelineStepTone }>> = {
  [PolicyChangeStatus.DECLINED]: { title: 'Declined', tone: 'danger' },
  [PolicyChangeStatus.CANCELED]: { title: 'Canceled', tone: 'neutral' },
}

/** The eight steps every request runs, with the day each was reached. */
export function PolicyChangeProgress({ request }: { request: PolicyChangeRequest }) {
  const steps: { title: string; at: string | null }[] = [
    { title: 'Requested', at: request.submittedAt },
    { title: 'Reviewed by your insurance broker', at: request.sentToCarrierAt },
    { title: 'Sent to your insurance providers', at: request.sentToCarrierAt },
    { title: request.priceUnchanged ? 'No price change' : 'Quoted', at: request.quotesPostedAt },
    { title: request.priceUnchanged ? 'Approved' : 'Your decision', at: request.decidedAt },
    { title: 'In place', at: request.boundAt },
    { title: 'Policies updated', at: request.policyUpdatedAt },
    { title: 'New COI', at: request.completedAt },
  ]
  const complete = request.status === PolicyChangeStatus.COMPLETE
  const stopped = STOPPED[request.status]
  // A sent request the customer called off was withdrawn, not canceled.
  const stoppedTitle =
    request.status === PolicyChangeStatus.CANCELED && request.submittedAt ? WITHDRAWN_LABEL : stopped?.title

  function stateFor(step: number): TimelineStepState {
    if (complete || step < request.step) return 'done'
    if (step > request.step) return 'pending'
    return stopped ? 'done' : 'current'
  }

  return (
    <Timeline>
      {steps.map(({ title, at }, i) => {
        const step = i + 1
        const stoppedHere = stopped && step === request.step
        return (
          <TimelineStep
            key={title}
            title={stoppedHere ? stoppedTitle : title}
            meta={at ? formatDate(at) : undefined}
            metaPosition="below"
            state={stateFor(step)}
            tone={stoppedHere ? stopped.tone : undefined}
          />
        )
      })}
    </Timeline>
  )
}
